/* eslint-disable react-hooks/exhaustive-deps */
import HeaderBasicLayout from 'src/layouts/BasicLayout/Header';
import {
  Box,
  useTheme,
  styled,
  Stack,
  Divider,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router';
import PlanCard from './components/PlanCard';
import { useAppSelector } from 'src/app/hooks';
import useBillingSystem from 'src/services/useBillingSystem';
import { useEffect, useMemo, useState } from 'react';
import useRegister from 'src/services/useRegister';
import {
  PeriodSubscription,
  PlanItem,
  PlanSimulationResponse,
  UserPackagePlan
} from 'src/models/register';
import PackagePlanSummary from 'src/components/PackagePlanSummary';
import { useAlert } from 'src/hooks/useAlert';
import { CustomGraphQLError } from 'src/models/general';
import { getPackageColor } from '../CurrentPlan';

const FeatureList = styled(List)(() => ({
  flexGrow: 1
}));

const CustomItemText = styled(ListItemText)(() => ({
  fontSize: '14px',
  fontWeight: '600'
}));

const CustomPaper = styled(Paper)(({ theme }) => ({
  borderRadius: `${theme.spacing(2)}`,
  width: '100%',
  maxWidth: '1000px',
  backgroundColor: `${theme.palette.grey[50]}`,
  boxShadow: '0px 2px 90px 12px rgba(5, 29, 61, 0.08)'
}));

const ButtonPeriod = styled(Button)(
  ({ theme }) => `
       background: ${theme.colors.gradients.blue1};
       &:hover {
        background: ${theme.colors.gradients.blue1};
       }
    `
);

const Container = styled(Box)(
  ({ theme }) => `
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: ${theme.spacing(3, 0, 3, 0)};
    `
);

export interface IPeriodOption {
  id: string;
  isActive: boolean;
  label: string;
  value: number;
  period: string;
}

export interface IPlanSummary {
  selectedPlan: UserPackagePlan | null;
  planItems: PlanItem[];
  promoCodes: string[];
  planSimulation: PlanSimulationResponse | null;
  referralCode?: string;
}

interface IProps {
  title?: string;
  hideCTAButtons?: boolean;
  hideClickBack?: boolean;
  onClickPlan?: (plan: UserPackagePlan, periodValue: string) => void;
}

export const INIT_PERIOD_OPTIONS: IPeriodOption[] = [
  {
    id: 'monthly',
    isActive: true,
    label: 'Monthly',
    value: 1,
    period: 'month'
  },
  // {
  //   id: 'quarterly',
  //   isActive: false,
  //   label: 'Quarterly',
  //   value: 3,
  //   period: '3 month'
  // },
  {
    id: 'biannually',
    isActive: false,
    label: 'Biannually',
    value: 6,
    period: '6 month'
  },
  {
    id: 'annually',
    isActive: false,
    label: 'Annualy',
    value: 12,
    period: 'year'
  }
];

const ChooseYourPlan = ({
  title = 'Choose Your Plan',
  hideCTAButtons = false,
  hideClickBack = false,
  onClickPlan
}: IProps) => {
  const navigation = useNavigate();
  const theme = useTheme();
  const { handleClickAlert } = useAlert();
  const projectId = useAppSelector((state) => state.users.project?.id);
  const userPackagePlan = useAppSelector(
    (state) => state.register.userPackagePlan
  );

  const {
    createUpgradePlanPaymentLink,
    responseUpgradePlanPaymentLink,
    useGetUserSubscription
  } = useBillingSystem();

  const { getUserPackagePlan } = useRegister();

  const { data: subscriptionData, loading: isLoading } = useGetUserSubscription(
    { variables: { projectId: projectId }, skip: !projectId }
  );

  const currentPlan =
    subscriptionData?.billing_getUserSubscription?.currentUserCycle
      ?.packagePlan;
  const nextSubs = subscriptionData?.billing_getUserSubscription?.nextUserCycle;
  const currentCycleNumber =
    subscriptionData?.billing_getUserSubscription?.currentUserCycle
      ?.userSubscriptionCycle?.cycleNumber;
  const referalCode =
    currentCycleNumber <= 1
      ? subscriptionData?.billing_getUserSubscription?.referalCode
      : '';

  const currentPlanIndex = userPackagePlan.data.findIndex(
    (plan) => plan.displayName === currentPlan?.displayName
  );

  // filter out plans that are the same or lower than current plan
  const filteredUserPackagePlan = useMemo(() => {
    if (userPackagePlan.data.length === 0) return [];
    return userPackagePlan.data.filter((_, index) => index >= currentPlanIndex);
  }, [userPackagePlan.data, currentPlanIndex]);

  const [periodes, setPeriodes] =
    useState<IPeriodOption[]>(INIT_PERIOD_OPTIONS);

  // for checkout plan
  const [planSummary, setPlanSummary] = useState<IPlanSummary | null>(null);

  const activePeriod = useMemo(
    () => periodes.find((period) => period.isActive === true),
    [periodes]
  );

  const handleChangePeriod = (id: string) => {
    setPeriodes((prevPeriodes) =>
      prevPeriodes.map((period) => {
        if (period.id === id) return { ...period, isActive: true };
        return { ...period, isActive: false };
      })
    );
  };

  const handleChoosePlan = (
    plan: UserPackagePlan,
    promoCodes: string[],
    planItems: PlanItem[],
    planSimulation: PlanSimulationResponse,
    referalCode: string
  ) => {
    if (onClickPlan) {
      onClickPlan(plan, activePeriod.id);
    } else
      setPlanSummary({
        selectedPlan: plan,
        promoCodes,
        planItems,
        planSimulation,
        referralCode: referalCode
      });
  };

  const handleCheckout = () => {
    if (!nextSubs) return;
    const isCustomPlan = planSummary.selectedPlan.name === 'custom';
    const isReferalCodeValid =
      planSummary.planSimulation.isReferralValid &&
      planSummary.planSimulation.referralCode.length;

    createUpgradePlanPaymentLink({
      variables: {
        userSubscriptionCycleId: nextSubs.userSubscriptionCycle.id,
        periodSubscription: activePeriod.id as PeriodSubscription,
        planItems: isCustomPlan ? planSummary!.planItems : null,
        promoCodes:
          planSummary!.planSimulation.promoCodes.length > 0
            ? planSummary!.planSimulation.promoCodes
            : undefined,
        referalCode: isReferalCodeValid
          ? planSummary!.planSimulation.referralCode
          : undefined,
        packagePlanId: !isCustomPlan ? planSummary.selectedPlan.id : null
      },
      onCompleted: (data) => {
        if (!data) return;
        window.location.href = data.billing_createPaymentLinkBilling.url;
      }
    });
  };

  // show error message from backend for checkout failure
  useEffect(() => {
    if (!responseUpgradePlanPaymentLink.error) return;
    const [gqlError] = responseUpgradePlanPaymentLink.error
      .graphQLErrors as CustomGraphQLError[];
    if (!gqlError) return;
    const [failure] = gqlError?.extensions?.reason?.failures;

    if (failure) {
      console.error('Billing error:', failure);
      handleClickAlert({
        message: failure,
        horizontal: 'center',
        vertical: 'top',
        severity: 'error'
      });
    } else {
      console.error('Unexpected error:', gqlError);
    }
  }, [responseUpgradePlanPaymentLink.error]);

  // get package list
  useEffect(() => {
    getUserPackagePlan();
  }, []);

  return (
    <>
      {hideClickBack ? (
        <Container>
          <Box sx={{ mb: theme.spacing(1) }}>
            <Typography variant="h2">
              Plans That Scale With Your Need
            </Typography>
          </Box>
        </Container>
      ) : (
        <HeaderBasicLayout
          title={planSummary ? 'Checkout' : title}
          onClickBack={
            !hideClickBack
              ? () => (planSummary ? setPlanSummary(null) : navigation(-1))
              : null
          }
        />
      )}
      {!planSummary ? (
        <>
          {/* Period Selection */}
          <Box
            sx={{
              height: theme.spacing(5),
              width: '500px',
              background: '#F5F5F5',
              borderRadius: '100px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mx: 'auto'
            }}
          >
            <Grid container>
              {periodes.map(({ id, label, isActive }) => {
                if (isActive)
                  return (
                    <Grid item xs={4} key={id}>
                      <ButtonPeriod variant="contained" fullWidth>
                        {label}
                      </ButtonPeriod>
                    </Grid>
                  );
                return (
                  <Grid item xs={4} key={id}>
                    <Button
                      variant="text"
                      fullWidth
                      onClick={() => handleChangePeriod(id)}
                    >
                      {label}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
          {/* Plan Cards */}
          <Box
            display="flex"
            justifyContent="center"
            mt={hideClickBack ? theme.spacing(0) : theme.spacing(8)}
            width="100%"
          >
            <CustomPaper elevation={0}>
              <Stack
                direction="row"
                divider={<Divider orientation="vertical" flexItem />}
              >
                {filteredUserPackagePlan.map((plan, i) => (
                  <Box
                    key={plan.id}
                    flexGrow={1}
                    bgcolor={theme.palette.grey[50]}
                    paddingX={5}
                    paddingTop={4}
                    paddingBottom="37px"
                  >
                    <PlanCard
                      plan={plan}
                      backgroundColor={getPackageColor(plan.displayName)}
                      period={activePeriod}
                      handleChoosePlan={handleChoosePlan}
                      hideCTAButton={hideCTAButtons}
                    />
                  </Box>
                ))}
              </Stack>
              <Divider />
              <Box paddingX={'50px'} paddingY={'30px'}>
                <Typography
                  variant="subtitle1"
                  component="h2"
                  fontWeight={700}
                  fontSize="18px"
                  marginBottom={theme.spacing(1.8)}
                  color="#0B85AE"
                >
                  All Plans Includes:
                </Typography>
                <Stack direction="row">
                  <FeatureList disablePadding>
                    <ListItem disablePadding>
                      <CustomItemText
                        primary="Content Analysis"
                        disableTypography
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <CustomItemText
                        primary="Community Tracking"
                        disableTypography
                      />
                    </ListItem>
                  </FeatureList>
                  <FeatureList disablePadding>
                    <ListItem disablePadding>
                      <CustomItemText
                        primary="Campaign Optimization"
                        disableTypography
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <CustomItemText
                        primary="Conversation Analysis"
                        disableTypography
                      />
                    </ListItem>
                  </FeatureList>
                  <FeatureList disablePadding>
                    <ListItem disablePadding>
                      <CustomItemText
                        primary="Unlimited Data & Users"
                        disableTypography
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <CustomItemText
                        primary="CSV Download"
                        disableTypography
                      />
                    </ListItem>
                  </FeatureList>
                </Stack>
              </Box>
            </CustomPaper>
          </Box>
        </>
      ) : (
        <Stack direction="column" spacing={2} alignItems="center" mt={4}>
          <PackagePlanSummary
            plan={planSummary.selectedPlan!}
            planSimulation={planSummary.planSimulation!}
            period={activePeriod!}
            promoCodes={planSummary.promoCodes}
            planItems={planSummary.planItems}
            onPromoUpdate={(updatedSimulation) => {
              setPlanSummary((prev) => ({
                ...prev!,
                planSimulation: updatedSimulation
              }));
            }}
            onReferalUpdate={(updatedSimulation) => {
              setPlanSummary((prev) => ({
                ...prev!,
                planSimulation: updatedSimulation
              }));
            }}
            referralCode={referalCode ?? ''}
          />
          <Box width="100%" maxWidth="400px">
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ width: '100%' }}
              onClick={() => handleCheckout()}
              disabled={responseUpgradePlanPaymentLink.loading}
            >
              {responseUpgradePlanPaymentLink.loading
                ? 'Loading..'
                : 'Make Payment'}
            </Button>
          </Box>
        </Stack>
      )}
    </>
  );
};

export default ChooseYourPlan;
